import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DispatchType } from "@bridgesplit/react";
import { isAll } from "@bridgesplit/utils";

/**
 * Chunked Pagination Controller
 *
 * This controller implements an over-fetching strategy for pagination where:
 * - Client sees `pageSize` records per page
 * - Backend receives `pageSize * CACHE_MULTIPLIER` records per request (chunk size)
 * - TParams must have `page` and `pageSize` properties that get replaced with chunk-based values
 *
 * How it works:
 * 1. Client page/pageSize control what the user sees
 * 2. Backend pagination uses chunkIndex (page / CACHE_MULTIPLIER) and fetchSize (pageSize * CACHE_MULTIPLIER)
 * 3. When user approaches cache boundary ((page + CHUNK_BUFFER) * pageSize >= cached data),
 *    chunkIndex increments to trigger refetch
 * 4. This ensures smooth pagination while minimizing API calls
 *
 * Example: User sees 5 records per page, but backend fetches 20 records (4x cache multiplier). When user is 5 records away from the end of the page, the backend will fetch the next chunk.
 */
export interface ChunkedPaginationState<TData, TParams> {
    page: number;
    pageSize: number;
    setPage: DispatchType<number>;
    setPageSize: DispatchType<number>;

    backendPagination: TParams; //Params sent to backend with page and pageSize replaced to be chunkIndex and fetchSize
    clientPagination: TParams; //Params sent to client with page and pageSize replaced to be chunkIndex and fetchSize

    cacheMultiplier: number;
    chunkBuffer: number;
    shouldRefetch: boolean;

    visibleData: (data: TData[] | undefined) => TData[] | undefined;
    resetPagination: () => void;
    clampPageToCount: (totalCount: number | undefined) => void;
}

export function useChunkedPaginationController<TData, TParams extends { page: number; pageSize: number }>(
    baseParams: Omit<TParams, "page" | "pageSize">,
    defaultPageSize = 5,
    cacheMultiplier = 4,
    chunkBuffer = 1
): ChunkedPaginationState<TData, TParams> {
    const [page, setPage] = useState(0); //Client page
    const [pageSize, setPageSize] = useState(defaultPageSize); //Client page size

    const chunkIndex = getChunkIndex(page, cacheMultiplier);
    const fetchSize = pageSize * cacheMultiplier;

    const backendPagination = useMemo(() => {
        return {
            ...baseParams,
            page: chunkIndex,
            pageSize: fetchSize
        } as TParams;
    }, [baseParams, chunkIndex, fetchSize]);

    const shouldRefetch = (page + chunkBuffer) * pageSize >= fetchSize; //Used for logging. The actual invalidation is from chunkIndex and the RTK query cache.

    const visibleData = useCallback(
        (data: TData[] | undefined) => {
            if (!data) return undefined;
            const pageInChunk = page % cacheMultiplier;
            const start = pageInChunk * pageSize;
            return data.slice(start, start + pageSize);
        },
        [page, pageSize, cacheMultiplier]
    );

    const resetPagination = useCallback(() => {
        setPage(0);
    }, []);

    const clampPageToCount = useCallback(
        (totalCount: number | undefined) => {
            if (totalCount === undefined) return;
            const lastPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1);
            if (page > lastPage) {
                setPage(lastPage);
            }
        },
        [page, pageSize]
    );

    const clientPagination = useMemo(() => {
        return {
            pageSize,
            page,
            ...baseParams
        } as TParams;
    }, [pageSize, page, baseParams]);

    return {
        page,
        pageSize,
        setPage,
        setPageSize,
        backendPagination,
        clientPagination,
        cacheMultiplier,
        chunkBuffer,
        shouldRefetch,
        visibleData,
        resetPagination,
        clampPageToCount
    };
}

export function useClampPaginationOnCountChange(
    clampPageToCount: ((totalCount: number | undefined) => void) | undefined,
    totalCount: number | undefined
) {
    useEffect(() => {
        if (!clampPageToCount) return;
        clampPageToCount(totalCount);
    }, [clampPageToCount, totalCount]);
}

// Generic chunked-pagination slice. The `visibleData` exposed by useChunkedPaginationController
// is bound to the controller's TData generic, so callers slicing a different array shape
// (e.g. ApiHistoricalLoanItem instead of AbfLoanExpanded) can use this instead.
export function sliceForClientPage<T>(
    data: T[] | undefined,
    pagination: { page: number; pageSize: number; cacheMultiplier: number }
): T[] | undefined {
    if (!data) return undefined;
    const pageInChunk = pagination.page % pagination.cacheMultiplier;
    const start = pageInChunk * pagination.pageSize;
    return data.slice(start, start + pagination.pageSize);
}

export interface TableChunkedPagination {
    page: number; // client page (drives the standalone <Pagination>)
    setPage: DispatchType<number>;
    backendPage: number; // chunkIndex to send to the backend
    fetchSize: number; // pageSize * cacheMultiplier to send to the backend
    pageSize: number;
    cacheMultiplier: number;
    // Slice a fetched chunk down to the visible client page. Pass the query's `isFetching` so that while a
    // new chunk loads we keep slicing the still-present old chunk at its own offset, avoiding a flash of the
    // wrong rows when paging across a chunk boundary.
    visibleData: <T>(data: T[] | undefined, isFetching?: boolean) => T[] | undefined;
    clampToCount: (totalCount: number | undefined) => void;
}

/**
 * Owns the client `page` for a Table rendered with `type: "managed"` plus a standalone `<Pagination>`,
 * while fetching from the backend with the same chunked over-fetching strategy as
 * {@link useChunkedPaginationController}: the backend is queried one chunk (`pageSize * cacheMultiplier`)
 * at a time and each client page is sliced from that chunk, so paging within a chunk does not refetch.
 * Unlike `useChunkedPaginationController` it does not assemble a params object, so it composes with hooks
 * that take positional page/pageSize args (e.g. `useEarnWithdrawalQueue`).
 */
export function useTableChunkedPagination(pageSize: number, cacheMultiplier = 4): TableChunkedPagination {
    const [page, setPage] = useState(0);
    // Last page whose chunk finished loading. Tracked in a ref so `visibleData` can keep slicing the old
    // chunk while the next one fetches, without forcing the slicing through the constructor (the caller's
    // query — and therefore its `isFetching` — is only available after this hook returns `backendPage`).
    const loadedPageRef = useRef(0);

    const visibleData = useCallback(
        <T>(data: T[] | undefined, isFetching = false) => {
            const crossingChunk =
                getChunkIndex(page, cacheMultiplier) !== getChunkIndex(loadedPageRef.current, cacheMultiplier);
            const activePage = isFetching && crossingChunk ? loadedPageRef.current : page;
            if (!isFetching) loadedPageRef.current = page;
            return sliceForClientPage(data, { page: activePage, pageSize, cacheMultiplier });
        },
        [page, pageSize, cacheMultiplier]
    );

    const clampToCount = useCallback(
        (totalCount: number | undefined) => {
            if (totalCount === undefined) return;
            const lastPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1);
            setPage((prev) => (prev > lastPage ? lastPage : prev));
        },
        [pageSize]
    );

    return {
        page,
        setPage,
        backendPage: getChunkIndex(page, cacheMultiplier),
        fetchSize: pageSize * cacheMultiplier,
        pageSize,
        cacheMultiplier,
        visibleData,
        clampToCount
    };
}

function getChunkIndex(page: number, cacheMultiplier: number) {
    return Math.floor(page / cacheMultiplier);
}

//Backend handles ALL_VALUE as undefined
export function getPrincipalMints(principalMint: string | undefined) {
    if (!principalMint || isAll(principalMint)) {
        return undefined;
    }
    return [principalMint];
}
